import { Check, ChevronRight, Copy } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/** Humanize a tool name (`get_rfq_status` → `Looked up rfq status`). */
function chipLabel(toolName: string): string {
  const words = toolName.replace(/^get_/, "looked up ").replace(/_/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * A collapsible "shows its work" chip for a `tool_call` / `tool_result` part.
 * Collapsed by default — the transcript stays prose-first; expanding reveals
 * the raw JSON for debugging. No Radix dependency: a local toggle is enough
 * for a non-interactive disclosure.
 */
export function MaxwellToolChip({
  toolName,
  payload,
}: {
  toolName: string;
  payload: unknown;
}) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const copyPayload = () => {
    navigator.clipboard
      .writeText(JSON.stringify(payload, null, 2))
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => toast.error("Failed to copy the payload."));
  };

  return (
    <div className="max-w-[85%] self-start">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex cursor-pointer items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
      >
        <ChevronRight
          size={12}
          className={cn("transition-transform", open && "rotate-90")}
        />
        {chipLabel(toolName)}
      </button>
      {open && (
        <div className="relative mt-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-1 top-1"
            aria-label="Copy payload"
            onClick={copyPayload}
          >
            {copied ? <Check size={12} /> : <Copy size={12} />}
          </Button>
          <pre className="scrollbar-thin max-h-48 overflow-auto rounded-md border border-border bg-card px-2 py-1.5 text-xs text-foreground">
            {JSON.stringify(payload, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}
